import { Fragment, type ReactNode, useId, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router";

import { PAGE_BLEED } from "@/components/page";
import { isNavItemCurrent, type NavItem } from "@/lib/navigation";
import { revealScrollLeft } from "@/lib/reveal";
import { cn } from "@/lib/utils";

/** A group of links in an area's own navigation; without a label it shows none. */
export interface SectionNavGroup {
  /** In the dashboard's language. */
  label?: string;
  items: NavItem[];
}

interface SectionLayoutProps {
  /** The navigation landmark's accessible name ("Settings", "Contents"). */
  label: string;
  /** The links, in groups, in order. */
  groups: readonly SectionNavGroup[];
  /**
   * When narrow, the tabs wrap onto a second row instead of scrolling
   * sideways, so every link stays in sight: for a list read in order, such as
   * the docs' pages.
   */
  wrapTabs?: boolean;
  /** The part that is open. */
  children: ReactNode;
}

/**
 * An area with its own navigation beside what is open in it, as Settings and
 * the docs have. The navigation is a column beside the content when the area
 * has room for both (1000 px, as on a wide window or beside the collapsed
 * sidebar), and a row of tabs above it otherwise, so the content keeps its
 * width. The area's own width decides (a container query), so the sidebar's
 * state counts too. The app's sidebar stays as it is.
 */
export function SectionLayout({ label, groups, wrapTabs = false, children }: SectionLayoutProps) {
  return (
    <div className="@container">
      <div className="flex flex-col gap-6 @min-[1000px]:flex-row @min-[1000px]:gap-10">
        <SectionNav label={label} groups={groups} wrapTabs={wrapTabs} />
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * The area's navigation, one landmark: with room a column of links under
 * their group labels; with less, one row of tabs above the content, a thin
 * line between the groups, that scrolls sideways on a phone, scrolled so the
 * open part's tab shows (or wraps, with `wrapTabs`). Only one of the two is
 * displayed at a time; the other is `display: none`, so neither screen
 * readers nor the keyboard meet it.
 */
function SectionNav({ label, groups, wrapTabs }: Omit<SectionLayoutProps, "children">) {
  const { pathname } = useLocation();
  const tabsRef = useRef<HTMLUListElement>(null);
  // Every link's label, in order: a language switch changes them on the same
  // path, and longer labels can push the open tab out of view.
  const labels = groups.flatMap((group) => group.items.map((item) => item.label)).join("\n");

  // On a phone a scrolling row of tabs is wider than the screen: bring the
  // open part's tab into view, sideways only, before the first paint, so it
  // never jumps. Again after a language switch, and whenever the row changes
  // width: a phone turned upright, or a window narrowed until the tabs
  // replace the column. Nothing moves when it shows already, when the tabs
  // wrap, or on a desktop.
  useLayoutEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) {
      return;
    }
    const reveal = () => {
      const current = tabs.querySelector<HTMLElement>('[aria-current="page"]');
      if (!current || tabs.scrollWidth <= tabs.clientWidth) {
        return;
      }
      // Where the tab sits in the list's scrolling content, from the list's own start.
      const start =
        current.getBoundingClientRect().left - tabs.getBoundingClientRect().left + tabs.scrollLeft;
      const margin = parseFloat(getComputedStyle(tabs).paddingLeft) || 0;
      tabs.scrollLeft = revealScrollLeft(
        { start, end: start + current.offsetWidth },
        tabs.scrollLeft,
        tabs.clientWidth,
        margin,
      );
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(tabs);
    return () => observer.disconnect();
  }, [pathname, labels]);

  return (
    <nav aria-label={label} className="@min-[1000px]:w-48 @min-[1000px]:shrink-0">
      <ul
        ref={tabsRef}
        className={cn(
          "flex items-center gap-1 @min-[1000px]:hidden",
          wrapTabs ? "flex-wrap" : cn("scrollbar-themed overflow-x-auto pb-1", PAGE_BLEED),
        )}
      >
        {groups.map((group, index) => (
          <Fragment key={group.items[0]?.to}>
            {index > 0 && <li aria-hidden="true" className="bg-border mx-2 h-5 w-px shrink-0" />}
            {group.items.map((item) => (
              <li key={item.to} className="shrink-0">
                <SectionLink item={item} tab />
              </li>
            ))}
          </Fragment>
        ))}
      </ul>
      <div className="hidden flex-col gap-5 @min-[1000px]:flex">
        {groups.map((group) => (
          <SectionGroup key={group.items[0]?.to} group={group} />
        ))}
      </div>
    </nav>
  );
}

/** A group of links under its small label, which names the list for screen readers. */
function SectionGroup({ group }: { group: SectionNavGroup }) {
  const labelId = useId();
  return (
    <div className="flex flex-col gap-0.5">
      {group.label && (
        <p id={labelId} className="text-muted px-3 pb-1 text-xs font-semibold">
          {group.label}
        </p>
      )}
      <ul aria-labelledby={group.label ? labelId : undefined} className="flex flex-col gap-0.5">
        {group.items.map((item) => (
          <li key={item.to}>
            <SectionLink item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SectionLinkProps {
  item: NavItem;
  /** In the row of tabs: on one line. In the column a long label wraps, its icon on the first line. */
  tab?: boolean;
}

/** A link of the area; the current one carries `aria-current="page"` and the blue tint. */
function SectionLink({ item, tab = false }: SectionLinkProps) {
  const active = isNavItemCurrent(item, useLocation().pathname);
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "focus-visible:ring-ring flex gap-2.5 rounded-md px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none pointer-coarse:py-2.5",
        tab ? "items-center whitespace-nowrap" : "items-start",
        active
          ? "bg-accent-light text-primary font-semibold"
          : "text-foreground hover:bg-accent-light hover:text-primary font-medium",
      )}
    >
      {/* In the column, the icon sits on the first line's middle: (line height - icon) / 2 from the top. */}
      <Icon className={cn("size-4 shrink-0", !tab && "mt-0.5")} aria-hidden="true" />
      {item.label}
    </Link>
  );
}
